import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg, fmtMoney } from "../../lib/api";
import { serviceName, STATUS_STYLES } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Truck, MapPin, User, Clock, ExternalLink } from "lucide-react";
import {
  addMonths, addWeeks, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, format, isSameMonth, isSameDay, parseISO,
} from "date-fns";

export default function OpsCalendar() {
  const navigate = useNavigate();
  const [missions, setMissions] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month");
  const [dragged, setDragged] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const days = useMemo(() => {
    if (view === "day") return [cursor];
    if (view === "week") return eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) });
    return eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) });
  }, [cursor, view]);

  const byDay = (day) =>
    missions
      .filter((m) => m.scheduled_date && isSameDay(parseISO(m.scheduled_date), day))
      .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

  const nav = (dir) => {
    if (view === "month") setCursor(addMonths(cursor, dir));
    else if (view === "week") setCursor(addWeeks(cursor, dir));
    else setCursor(addDays(cursor, dir));
  };

  const onDrop = async (e, day) => {
    e.preventDefault();
    if (!dragged) return;
    const date = format(day, "yyyy-MM-dd");
    setMissions((ms) => ms.map((m) => (m.id === dragged ? { ...m, scheduled_date: date } : m)));
    try {
      await api.patch(`/missions/${dragged}`, { scheduled_date: date });
      toast.success("Mission rescheduled");
    } catch (err) {
      toast.error(errMsg(err));
      load();
    }
    setDragged(null);
  };

  const openMission = () => {
    if (!detail) return;
    navigate("/ops/missions", { state: { focusMissionId: detail.id } });
    setDetail(null);
  };

  const MissionChip = ({ m }) => (
    <div draggable onDragStart={() => setDragged(m.id)} onClick={() => setDetail(m)}
      data-testid={`calendar-mission-${m.mission_number}`}
      className={`rounded-md border px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-[11px] font-medium cursor-pointer active:opacity-70 truncate ${STATUS_STYLES[m.status] || ""}`}
      title={`${m.mission_number} · ${m.scheduled_time || ""} — ${m.customer_name} (${serviceName(m.service_type)})`}>
      <span className="font-mono-cc">{m.scheduled_time}</span> <span className="hidden sm:inline">{m.customer_name}</span>
    </div>
  );

  return (
    <div data-testid="ops-calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Scheduling</p>
          <h1 className="page-title mt-1.5">Calendar</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex rounded-full border overflow-hidden">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)}
                className={`px-4 py-1.5 text-xs font-semibold capitalize transition-colors duration-200 ${view === v ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)]" : "text-muted-foreground hover:text-foreground"}`}>
                {v}
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => nav(-1)} data-testid="calendar-prev-button"><ChevronLeft className="w-4 h-4" /></Button>
          <p className="font-heading font-semibold w-40 text-center" data-testid="calendar-cursor-label">
            {view === "month" ? format(cursor, "MMMM yyyy") : format(cursor, "MMM d, yyyy")}
          </p>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => nav(1)} data-testid="calendar-next-button"><ChevronRight className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setCursor(new Date())} data-testid="calendar-today-button">Today</Button>
        </div>
      </div>

      {view === "month" && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-7 border-b">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <p key={d} className="px-1 sm:px-3 py-2 overline-cc text-center text-[10px] sm:text-xs">
                <span className="sm:hidden">{d[0]}</span>
                <span className="hidden sm:inline">{d}</span>
              </p>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => (
              <div key={day.toISOString()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, day)}
                className={`min-w-0 min-h-[80px] sm:min-h-[110px] border-b border-r p-1 sm:p-1.5 space-y-1 overflow-hidden ${!isSameMonth(day, cursor) ? "opacity-40" : ""} ${isSameDay(day, new Date()) ? "bg-[var(--cc-accent)]/5" : ""}`}>
                <p className={`text-[10px] sm:text-xs font-mono-cc px-0.5 sm:px-1 ${isSameDay(day, new Date()) ? "text-[var(--cc-accent)] font-bold" : "text-muted-foreground"}`}>{format(day, "d")}</p>
                {byDay(day).map((m) => <MissionChip key={m.id} m={m} />)}
              </div>
            ))}
          </div>
        </div>
      )}

      {(view === "week" || view === "day") && (
        <div className={`grid gap-3 ${view === "week" ? "grid-cols-1 md:grid-cols-7" : "grid-cols-1"}`}>
          {days.map((day) => (
            <div key={day.toISOString()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, day)}
              className={`rounded-xl border bg-card p-3 min-h-[240px] ${isSameDay(day, new Date()) ? "border-[var(--cc-accent)]/50" : ""}`}>
              <p className="overline-cc mb-3">{format(day, "EEE d")}</p>
              <div className="space-y-2">
                {byDay(day).map((m) => (
                  <div key={m.id} draggable onDragStart={() => setDragged(m.id)} onClick={() => setDetail(m)}
                    data-testid={`calendar-mission-${m.mission_number}`}
                    className={`rounded-lg border p-2.5 cursor-pointer active:opacity-70 ${STATUS_STYLES[m.status] || ""}`}>
                    <p className="font-mono-cc text-[10px]">{m.mission_number} · {m.scheduled_time}</p>
                    <p className="text-xs font-semibold mt-0.5">{m.customer_name}</p>
                    <p className="text-[11px] opacity-80">{serviceName(m.service_type)}</p>
                  </div>
                ))}
                {byDay(day).length === 0 && <p className="text-xs text-muted-foreground">—</p>}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground mt-4 font-mono-cc">TIP: TAP A MISSION TO VIEW · DRAG TO RESCHEDULE.</p>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="calendar-mission-dialog" aria-describedby={undefined}>
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono-cc text-xs opacity-70">{detail.mission_number}</span>
                  <StatusBadge status={detail.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="font-semibold">{detail.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{detail.customer_email || ""}{detail.customer_phone ? ` · ${detail.customer_phone}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Truck className="w-4 h-4 text-muted-foreground" />
                  <p>{serviceName(detail.service_type)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <p>{detail.scheduled_date} · {detail.scheduled_time || "TBD"}</p>
                </div>
                {(detail.pickup_address || detail.dropoff_address) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      {detail.pickup_address && <p>{detail.pickup_address}</p>}
                      {detail.dropoff_address && <p>→ {detail.dropoff_address}</p>}
                    </div>
                  </div>
                )}
                <div className="rounded-xl border bg-secondary/40 p-4 flex justify-between items-center">
                  <span className="overline-cc">Amount</span>
                  <span className="font-heading font-extrabold text-2xl">{fmtMoney(detail.amount || 0)}</span>
                </div>
                {detail.distance_miles > 0 && (
                  <p className="text-xs text-muted-foreground font-mono-cc">ROUTE — {detail.distance_miles} MI</p>
                )}
                <div className="flex gap-2 pt-2">
                  <Button data-testid="calendar-open-job" onClick={openMission}
                    className="flex-1 rounded-full btn-glow bg-[var(--cc-accent)] text-[var(--cc-accent-fg)] hover:bg-[var(--cc-accent)]/90 font-semibold">
                    Open Job <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
